class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.onBodyClick = this.handleBodyClick.bind(this);
  }

  connectedCallback() {
    // Ensure the <cart-notification> element is top-level in document.body
    // so its popup (#cart-notification) and overlay are never trapped in header/section stacking contexts
    if (this.parentNode !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.notification = document.getElementById('cart-notification');
    this.overlay = this.querySelector('.cart-notification-overlay');
    this.header = document.querySelector('sticky-header');

    // Clean up active states on initial page load / refresh
    document.body.classList.remove('cart-notification-open');
    this.classList.remove('active');
    this.removeAttribute('active');
    if (this.notification) this.notification.classList.remove('active', 'animate');
    if (this.overlay) this.overlay.classList.remove('active');

    this.notification?.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    if (this.overlay) {
      this.overlay.addEventListener('click', (evt) => {
        evt.stopPropagation();
        this.close();
      });
    }
  }

  disconnectedCallback() {
    document.body.classList.remove('cart-notification-open');
  }

  open() {
    this.classList.add('active');
    this.setAttribute('active', '');
    if (this.overlay) this.overlay.classList.add('active');
    document.body.classList.add('cart-notification-open');
    this.notification?.classList.add('animate', 'active');

    this.notification?.addEventListener(
      'transitionend',
      () => {
        this.notification?.focus();
        if (typeof trapFocus === 'function') trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const cartUrl = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');
      const response = await fetch(`${cartUrl}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.classList.remove('active');
    this.removeAttribute('active');
    if (this.overlay) this.overlay.classList.remove('active');
    document.body.classList.remove('cart-notification-open');
    this.notification?.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (container) {
        const sectionKey = section.section || section.id;
        if (parsedState.sections && parsedState.sections[sectionKey]) {
          container.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[sectionKey],
            section.selector
          );
        }
      }
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
        section: 'header',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (
      target.classList.contains('cart-notification-overlay') ||
      (!target.closest('#cart-notification') && !target.closest('cart-notification'))
    ) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
